/**
 * Job Management API
 *
 * API client functions for job categories, task types, and category-task
 * assignments used by the settings wizard.
 *
 * Backend: apps/backend/src/domains/works-lite/settings/routes.ts
 */

import { apiClient } from '@/lib/api';
import type {
  AssignTaskBody,
  CategoryTaskItem,
  CategoryTasksListResponse,
  CreateJobCategoryBody,
  CreateTaskTypeBody,
  JobCategoriesListResponse,
  JobCategoryItem,
  ReorderTasksBody,
  SetTaskFormsBody,
  TaskFormsResponse,
  TaskTypeItem,
  TaskTypesListResponse,
  UpdateCategoryTaskBody,
  UpdateJobCategoryBody,
  UpdateTaskTypeBody,
} from '@skewb-works/contracts';

export type {
  JobCategoryItem,
  JobCategoriesListResponse,
  CreateJobCategoryBody,
  UpdateJobCategoryBody,
  TaskTypeItem,
  TaskTypesListResponse,
  CreateTaskTypeBody,
  UpdateTaskTypeBody,
  CategoryTaskItem,
  CategoryTasksListResponse,
  AssignTaskBody,
  UpdateCategoryTaskBody,
  ReorderTasksBody,
};

const BASE = '/works-lite/settings';

// ---------------------------------------------------------------------------
// Job Categories
// ---------------------------------------------------------------------------

export async function getJobCategories(): Promise<JobCategoriesListResponse> {
  return apiClient.get<JobCategoriesListResponse>(`${BASE}/job-categories`);
}

export async function createJobCategory(body: CreateJobCategoryBody): Promise<JobCategoryItem> {
  return apiClient.post<JobCategoryItem>(`${BASE}/job-categories`, body);
}

export async function updateJobCategory(
  id: number,
  body: UpdateJobCategoryBody,
): Promise<JobCategoryItem> {
  return apiClient.put<JobCategoryItem>(`${BASE}/job-categories/${id}`, body);
}

export async function deleteJobCategory(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${BASE}/job-categories/${id}`);
}

// ---------------------------------------------------------------------------
// Task Types
// ---------------------------------------------------------------------------

export async function getTaskTypes(): Promise<TaskTypesListResponse> {
  return apiClient.get<TaskTypesListResponse>(`${BASE}/task-types`);
}

export async function createTaskType(body: CreateTaskTypeBody): Promise<TaskTypeItem> {
  return apiClient.post<TaskTypeItem>(`${BASE}/task-types`, body);
}

export async function updateTaskType(id: number, body: UpdateTaskTypeBody): Promise<TaskTypeItem> {
  return apiClient.put<TaskTypeItem>(`${BASE}/task-types/${id}`, body);
}

export async function deleteTaskType(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${BASE}/task-types/${id}`);
}

// ---------------------------------------------------------------------------
// Category Tasks
// ---------------------------------------------------------------------------

export async function getCategoryTasks(categoryId: number): Promise<CategoryTasksListResponse> {
  return apiClient.get<CategoryTasksListResponse>(`${BASE}/category-tasks/${categoryId}`);
}

export async function assignTask(body: AssignTaskBody): Promise<CategoryTaskItem> {
  return apiClient.post<CategoryTaskItem>(`${BASE}/category-tasks`, body);
}

export async function updateCategoryTask(
  id: number,
  body: UpdateCategoryTaskBody,
): Promise<CategoryTaskItem> {
  return apiClient.patch<CategoryTaskItem>(`${BASE}/category-tasks/${id}`, body);
}

export async function removeTaskAssignment(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${BASE}/category-tasks/${id}`);
}

export async function reorderTasks(body: ReorderTasksBody): Promise<{ message: string }> {
  return apiClient.put<{ message: string }>(`${BASE}/category-tasks/reorder`, body);
}

// ---- Task Forms ----

export async function getTaskForms(taskTypeId: number): Promise<TaskFormsResponse> {
  return apiClient.get<TaskFormsResponse>(`${BASE}/task-forms/${taskTypeId}`);
}

export async function setTaskForms(body: SetTaskFormsBody): Promise<TaskFormsResponse> {
  return apiClient.put<TaskFormsResponse>(`${BASE}/task-forms`, body);
}
